import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DropdownOption } from "@cms/shared-library";
import { TeamMemberModel } from 'src/app/models/team-member.model';
import { LookupObjectService } from 'src/app/services/lookup-object/lookup-object.service';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { emailValidatorPattern, phoneValidatorPattern } from 'src/app/utils/validators.util';

@Component({
    selector: 'app-add-team-member',
    templateUrl: './add-team-member.component.html',
    styleUrls: ['../signing-authority-information/signing-authority-information.component.scss', './add-team-member.component.scss'],
    standalone: false
})
export class AddTeamMemberComponent implements OnInit {

  @Input() showErrorsOverride: boolean = false
  @Input() displayHeader: string
  @Input() teamMember: TeamMemberModel
  @Input() HideAffiliatedRoles = false;
  @Input() soseMode = false;
  @Output() cancelClicked = new EventEmitter<boolean>()

  roleInOrganizationOptions: DropdownOption[] = []
  newTeamMemberForm: FormGroup
  additionalTeamMemberForm: FormGroup
  role: string = ""

  currentLang: string

  constructor(private formBuilder: FormBuilder,
    public translateService: TranslateService,
    @Inject('LookupObjectService') public lookupObjectService: LookupObjectService) {
    this.currentLang = translateService.currentLang
    translateService.onLangChange.subscribe(lang => {
      this.currentLang = lang.lang;
    });
  }

  ngOnInit(): void {
    this.lookupObjectService.getLookupObjectsByGroupName("AuthorityRole").subscribe(data => {
      this.roleInOrganizationOptions = data.filter(x => x.LookupObjectId.toLowerCase() != TrainingProgramApplicationUtils.DSA.toLowerCase() && x.LookupObjectId.toLowerCase() != TrainingProgramApplicationUtils.ASA.toLowerCase()).map(x => new DropdownOption(x.Value, x.ValueFr, x.LookupObjectId))
      if(this.HideAffiliatedRoles)
      {
        this.roleInOrganizationOptions = this.roleInOrganizationOptions.filter(x => x.name != TrainingProgramApplicationUtils.AffiliateAdministrator && x.name != TrainingProgramApplicationUtils.AffiliateInstructor );
      }
    })
    this.buildForm();
  }

  buildForm() {
    this.newTeamMemberForm = this.formBuilder.group({
      firstName: new FormControl('', [Validators.maxLength(256), Validators.required]),
      lastName: new FormControl('', [Validators.maxLength(256), Validators.required]),
      phone: new FormControl('', this.soseMode ? [Validators.maxLength(11), Validators.required, Validators.pattern(phoneValidatorPattern)] : [Validators.maxLength(11), Validators.pattern(phoneValidatorPattern)]),
      emailAddress: new FormControl('', [Validators.pattern(emailValidatorPattern), Validators.required]),
      role: new FormControl('', this.soseMode ? [] : [Validators.required])
    });
    if (this.soseMode) {
      this.newTeamMemberForm.addControl('middleInitial', new FormControl(''));
      this.newTeamMemberForm.addControl('position', new FormControl('', [Validators.required]));
    }
  }

  setTeamMemberInfo(teamMember: any) {
    this.newTeamMemberForm = this.formBuilder.group({
      firstName: new FormControl(teamMember.FirstName, [Validators.maxLength(256), Validators.required]),
      lastName: new FormControl(teamMember.LastName, [Validators.maxLength(256), Validators.required]),
      phone: new FormControl(teamMember.PhoneNumber.split('x')[0], this.soseMode ? [Validators.maxLength(11), Validators.required, Validators.pattern(phoneValidatorPattern)] : [Validators.maxLength(11), Validators.pattern(phoneValidatorPattern)]),
      emailAddress: new FormControl(teamMember.EmailAddress, [Validators.pattern(emailValidatorPattern), Validators.required]),
      role: new FormControl(teamMember.RoleIds, this.soseMode ? [] : [Validators.required])
    });
    if (this.soseMode) {
      this.newTeamMemberForm.addControl('middleInitial', new FormControl(teamMember.MiddleName));
      this.newTeamMemberForm.addControl('position', new FormControl(teamMember.Position, [Validators.required]));
    }
  }

  saveNewTeamMember() {
    this.teamMember = {
      firstName: this.newTeamMemberForm.controls['firstName'].value,
      lastName: this.newTeamMemberForm.controls['lastName'].value,
      middleName: this.soseMode ? this.newTeamMemberForm.controls['middleInitial']?.value ?? '' : '',
      phoneNumber: this.newTeamMemberForm.controls['phone'].value,
      phoneExtension: '',
      position: this.soseMode ? this.newTeamMemberForm.controls['position']?.value : '',
      emailAddress: this.newTeamMemberForm.controls['emailAddress'].value,
      roleIds: [this.role],
      expanded: this.teamMember.expanded
    };
    return this.teamMember
  }

  cancelForm() {
    this.teamMember.expanded = !this.teamMember.expanded;
    if (!this.teamMember.expanded) this.cancelClicked.emit(true)
    this.newTeamMemberForm.reset();
    this.newTeamMemberForm.controls['firstName'].clearValidators();
    this.newTeamMemberForm.controls['lastName'].clearValidators();
    this.newTeamMemberForm.controls['phone'].clearValidators();
    this.newTeamMemberForm.controls['emailAddress'].clearValidators();
    this.newTeamMemberForm.controls['role'].clearValidators();
    this.role = '';
    if (this.teamMember.expanded) {
      this.buildForm();
    }
  }

  isValid(): boolean {
    if (this.soseMode && !this.teamMember.expanded) {
      return true;
    } 
    return this.newTeamMemberForm.valid && (this.soseMode ? true : !TrainingProgramApplicationUtils.isNullOrEmpty(this.role));
  }
}
